import { useCallback, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/shadcn/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/shadcn/tabs";
import { LogsTab } from "@/components/Spotflow/WorkflowRunner/Outputs/LogsTab";
import { SpanViewer } from "@/components/OtelSpanViewer";
import { cn } from "@/lib/utils";
import { NoxusJsonView } from "@/components/UI/NoxusJsonView";
import { Check, ChevronsDownUp, ChevronsUpDown, Copy } from "lucide-react";
import { Button } from "@/components/shadcn/button";

interface DebugSheetProps {
  groupId: string;
  conversationId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  debugData: Record<string, unknown> | null;
  canViewLogs: boolean;
}

const MIN_WIDTH = 480;
const MAX_WIDTH_RATIO = 0.9;
const DEFAULT_WIDTH = 800;

type ExpandMode = "default" | "all" | "collapsed";

// Array → message → parts array visible; part payloads start collapsed.
const EXPAND_BY_MODE: Record<ExpandMode, "all" | number> = {
  default: 3,
  all: "all",
  collapsed: 1,
};

export function DebugSheet({
  groupId,
  conversationId,
  isOpen,
  onOpenChange,
  debugData,
  canViewLogs,
}: DebugSheetProps) {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [expandMode, setExpandMode] = useState<ExpandMode>("default");
  const [copied, setCopied] = useState(false);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(DEFAULT_WIDTH);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      startX.current = e.clientX;
      startWidth.current = width;

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragging.current) return;
        const delta = startX.current - ev.clientX;
        const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
        setWidth(
          Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth.current + delta))
        );
      };

      const onMouseUp = () => {
        dragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width]
  );

  const rawParts = debugData ? debugData["pydantic_message_parts"] : null;
  const messageParts = Array.isArray(rawParts) ? (rawParts as unknown[]) : null;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="p-0 sm:max-w-none flex flex-col gap-2"
        style={{ width: `${width}px` }}
      >
        {/* Resize handle */}
        <div
          className={cn(
            "absolute inset-y-0 left-0 w-1 cursor-col-resize z-10",
            "hover:bg-primary/20 active:bg-primary/30 transition-colors"
          )}
          onMouseDown={onMouseDown}
        />
        <SheetHeader className="border-0">
          <SheetTitle>Debug</SheetTitle>
        </SheetHeader>
        <Tabs
          defaultValue="messages"
          className="flex flex-col flex-1 min-h-0 gap-5"
        >
          <div className="px-6">
            <TabsList>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              {canViewLogs && <TabsTrigger value="logs">Logs</TabsTrigger>}
              {canViewLogs && <TabsTrigger value="traces">Traces</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent
            value="messages"
            className="flex-1 min-h-0 overflow-auto px-6 pb-6 pr-1"
            style={{ scrollbarGutter: "stable" }}
          >
            {messageParts ? (
              <div className="rounded-md border border-border bg-muted/20">
                <div className="flex items-center justify-between px-3 py-1 border-b border-border/40 bg-muted/30">
                  <span className="text-xs font-medium text-muted-foreground px-1">
                    {messageParts.length} message
                    {messageParts.length === 1 ? "" : "s"}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground"
                      onClick={() => setExpandMode("all")}
                    >
                      <ChevronsUpDown className="size-3.5 mr-1" />
                      Expand all
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground"
                      onClick={() => setExpandMode("collapsed")}
                    >
                      <ChevronsDownUp className="size-3.5 mr-1" />
                      Collapse all
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground"
                      onClick={async () => {
                        await navigator.clipboard.writeText(
                          JSON.stringify(messageParts, null, 2)
                        );
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      }}
                    >
                      {copied ? (
                        <Check className="size-3.5 mr-1" />
                      ) : (
                        <Copy className="size-3.5 mr-1" />
                      )}
                      {copied ? "Copied" : "Copy all"}
                    </Button>
                  </div>
                </div>
                <div className="px-3 py-2">
                  <NoxusJsonView
                    data={messageParts}
                    expand={EXPAND_BY_MODE[expandMode]}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-24 text-sm text-muted-foreground">
                No debug data available
              </div>
            )}
          </TabsContent>
          {canViewLogs && (
            <TabsContent
              value="logs"
              className="flex-1 min-h-0 overflow-hidden mx-6 border-1 border-border rounded-md"
            >
              <LogsTab
                fetchUrl={`/api/backend/groups/${groupId}/conversation/${conversationId}/execution-logs`}
                canView={true}
              />
            </TabsContent>
          )}
          {canViewLogs && (
            <TabsContent
              value="traces"
              className="flex-1 min-h-0 overflow-hidden mx-6 border-1 border-border rounded-md"
            >
              <SpanViewer
                fetchUrl={`/api/backend/groups/${groupId}/conversation/${conversationId}/spans`}
                enableTurnFilter
              />
            </TabsContent>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
